<script setup lang="ts">
import { Store } from 'lucide-vue-next'
import dayjs from 'dayjs'

import type { Survey } from '@/types/types'
const props = defineProps<{
  surveys: Survey[]
  currentPage?: number
  lastPage?: number
  total?: number
  isFetching?: boolean
}>()

const { t } = useI18n()
const router = useRouter()
const { stores } = useLocalStorage()

const goToDetails = (event: any) => {
  if (event && event.id) {
    router.push({ name: 'SurveyDetail', params: { surveyId: event.id } })
  }
}

const getStatusColor = (survey) => {
  if (survey.isTerminated) {
    return 'yellow'
  } else {
    if (survey.isActive) {
      return 'green'
    } else {
      return 'red'
    }
  }
}

const getStatusText = (survey: Survey) => {
  if (survey.isTerminated) {
    return t('pages.survey.surveyPage.items.status.terminated')
  } else {
    if (survey.isActive) {
      return t('pages.survey.surveyPage.items.status.enabled')
    } else {
      return t('pages.survey.surveyPage.items.status.disabled')
    }
  }
}

const columns = {
  question: {
    label: t('pages.survey.surveyPage.items.question'),
    headerClass: '!w-1/2 !min-w-[300px]'
  },
  launchDate: {
    label: t('pages.survey.surveyPage.items.launchDate')
  },
  endDate: {
    label: t('pages.survey.surveyPage.items.endDate')
  },
  status: {
    label: t('pages.survey.surveyPage.items.status.label')
  },
  stores: {
    label: t('pages.survey.surveyPage.items.stores')
  },
  interrogated: {
    label: t('pages.survey.surveyPage.items.stats.interrogated'),
    numeric: true
  },
  answerers: {
    label: t('pages.survey.surveyPage.items.stats.answerers'),
    numeric: true
  }
}

const rows = computed(() => {
  return props.surveys.map((survey) => {
    return {
      id: survey.id,
      data: {
        question: survey.questionText,
        launchDate: dayjs.unix(survey.launchDate).toDate().toLocaleDateString(),
        endDate: survey.endDate ? dayjs.unix(survey.endDate).toDate().toLocaleDateString() : null,
        status: {
          text: getStatusText(survey),
          color: getStatusColor(survey)
        },
        stores: (survey as any).stores || [],
        interrogated: survey.statistics?.surveyedQuantity,
        answerers: survey.statistics?.answeredQuantity
      }
    }
  })
})

const pagination = computed(() => ({
  currentPage: props.currentPage || 1,
  total: props.total || 1,
  lastPage: props.lastPage || 1
}))

const emit = defineEmits(['change-page'])
const getSurveys = (page) => {
  emit('change-page', page)
}
</script>

<template>
  <div>
    <AppTable
      :columns="columns"
      :rows="rows"
      :pagination="pagination"
      :is-loading="isFetching"
      row-classes="cursor-pointer"
      @update:change-page="getSurveys($event)"
      @row-clicked="goToDetails"
    >
      <template #td-question="{ data }">
        <div class="w-full whitespace-normal">
          <p class="text-sm font-semibold leading-5 text-foreground">
            {{ data.question }}
          </p>
        </div>
      </template>

      <template #td-launchDate="{ data }">
        <p class="text-sm font-medium leading-5 text-foreground">
          {{ data.launchDate }}
        </p>
      </template>

      <template #td-endDate="{ data }">
        <p v-if="data.endDate" class="text-sm font-medium leading-5 text-foreground">
          {{ data.endDate }}
        </p>
        <p v-else>-</p>
      </template>

      <template #td-status="{ data }">
        <BaseChip :text="data.status.text" :color="data.status.color" />
      </template>

      <template #td-stores="{ data }">
        <div class="flex items-center">
          <AppTooltip v-if="data.stores.length > 0" tooltip-position="bottom">
            <template #tooltip-content>
              <div class="flex flex-col gap-2 px-1 text-left">
                <span v-for="store in data.stores" :key="store.id" class="text-md">
                  {{ store.name }}
                </span>
              </div>
            </template>

            <BaseChip
              :text="
                data.stores.length === stores.length
                  ? t('pages.autopilot.autopilotsPage.items.allStores')
                  : t('pages.autopilot.autopilotsPage.items.store', { count: data.stores.length })
              "
            >
              <template #leading>
                <Store />
              </template>
            </BaseChip>
          </AppTooltip>
          <p v-else>-</p>
        </div>
      </template>
    </AppTable>
  </div>
</template>
